import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ChevronLeft } from 'lucide-react';
import { ContenusCours } from '../components/cours/ContenusCours';
import { EvaluationsCours } from '../components/cours/EvaluationsCours';
import { ProgrammeCours } from '../components/cours/ProgrammeCours';
import { ProgrammeEnLecture } from '../components/cours/ProgrammeEnLecture';
import { BoutonsStatutCours, PastilleStatutCours } from '../components/cours/StatutCours';
import { Chargement, MessageErreur, MarqueObligatoire } from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import { useCours, useEnregistrerInformations, useOptionsCours, type InformationsCours } from '../lib/useCours';

/**
 * Créer un cours (écran `instructor-create`, TF-442). Le cours naît à
 * l'enregistrement des informations générales, en brouillon ; son adresse
 * (`?cours=`) le rouvre ensuite. Le programme — modules et leçons — se range
 * au glisser-déposer et s'enregistre à chaque modification.
 *
 * Les contenus — vidéo et sous-titres de chaque leçon — se déposent à
 * l'étape 3 (TF-444), et `?etape=3` y ramène depuis l'aperçu du lecteur. Les
 * quiz et devoirs à rendre se créent à l'étape 4 (TF-454). L'en-tête publie,
 * archive ou désarchive (TF-443) ; un cours archivé s'affiche en lecture seule.
 */

const ETAPES = [
  { n: 1, t: 'Informations générales' },
  { n: 2, t: 'Programme pédagogique' },
  { n: 3, t: 'Contenus & supports' },
  { n: 4, t: 'Évaluations & quiz' },
] as const;

const CLASSE_CHAMP =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-codialis-mint';

const VIDE: InformationsCours = { titre: '', formationId: '', blocId: '', description: '' };

function etapeDemandee(valeur: string | null): number {
  const etape = Number(valeur);
  return Number.isInteger(etape) && etape >= 1 && etape <= ETAPES.length ? etape : 1;
}

function messageErreur(erreur: unknown): string {
  return erreur instanceof ErreurApi ? erreur.message : 'Enregistrement impossible. Réessayez.';
}

export function InstructorCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [parametres, setParametres] = useSearchParams();
  const coursId = parametres.get('cours');
  const cours = useCours(coursId);
  const options = useOptionsCours();
  const enregistrement = useEnregistrerInformations(coursId);
  const [etape, setEtape] = useState<number>(() => (coursId ? etapeDemandee(parametres.get('etape')) : 1));
  const [saisie, setSaisie] = useState<InformationsCours>(VIDE);

  // « Créer un cours » depuis le menu, un cours ouvert : on repart de zéro.
  useEffect(() => {
    if (coursId) return;
    setSaisie(VIDE);
    setEtape(1);
  }, [coursId]);

  // Le formulaire reprend le cours ouvert, une fois à son chargement.
  const charge = cours.data;
  useEffect(() => {
    if (!charge) return;
    setSaisie({
      titre: charge.titre,
      formationId: charge.formation.id,
      blocId: charge.bloc?.id ?? '',
      description: charge.description ?? '',
    });
  }, [charge?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Un cours neuf propose la première formation de la liste.
  const formations = useMemo(() => options.data?.formations ?? [], [options.data]);
  useEffect(() => {
    if (!coursId && !saisie.formationId && formations[0]) {
      setSaisie((courante) => ({ ...courante, formationId: formations[0]!.id }));
    }
  }, [coursId, formations, saisie.formationId]);

  // La formation d'un cours peut avoir été désactivée depuis : elle reste
  // affichée, avec le seul bloc que le cours couvre.
  const proposees =
    charge && !formations.some((formation) => formation.id === charge.formation.id)
      ? [...formations, { ...charge.formation, blocs: charge.bloc ? [charge.bloc] : [] }]
      : formations;
  const blocs = proposees.find((formation) => formation.id === saisie.formationId)?.blocs ?? [];

  async function enregistrer(etapeSuivante: number | null): Promise<void> {
    try {
      const enregistre = await enregistrement.mutateAsync(saisie);
      if (!coursId) setParametres({ cours: enregistre.id }, { replace: true });
      toast(coursId ? 'Informations du cours enregistrées' : 'Cours créé en brouillon');
      if (etapeSuivante) setEtape(etapeSuivante);
    } catch (erreur) {
      toast(messageErreur(erreur));
    }
  }

  const soumettre = (evenement: FormEvent) => {
    evenement.preventDefault();
    void enregistrer(2);
  };

  const existe = Boolean(charge);
  const lectureSeule = charge?.refusModification ?? null;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      {/* En-tête */}
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/instructor-courses')}
            className="rounded-lg p-2 text-codialis-gray hover:bg-gray-100 hover:text-codialis-navy"
            aria-label="Retour"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-extrabold text-codialis-navy">
              {charge ? charge.titre : 'Créer un cours'}
            </h1>
            <p className="mt-0.5 text-sm text-codialis-gray">
              Configurez votre cours étape par étape · le programme s’enregistre à chaque modification
            </p>
          </div>
        </div>
        {charge && (
          <div className="flex min-w-0 flex-col items-start gap-2 lg:items-end">
            <div className="flex flex-wrap items-center gap-2 text-xs text-codialis-gray">
              <PastilleStatutCours statut={charge.statut} libelle={charge.statutLibelle} />
              <span>{charge.formation.titre}</span>
            </div>
            <BoutonsStatutCours coursId={charge.id} gestes={charge.gestes} />
          </div>
        )}
      </div>

      {lectureSeule && (
        <p className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-900" role="note">
          {lectureSeule}
        </p>
      )}

      {/* Stepper */}
      <div className="rounded-2xl bg-white p-4 shadow-card sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {ETAPES.map((s) => {
            const accessible = s.n === 1 || existe;
            return (
              <button
                key={s.n}
                type="button"
                onClick={() => setEtape(s.n)}
                disabled={!accessible}
                title={accessible ? undefined : 'Enregistrez d’abord les informations générales'}
                aria-current={etape === s.n ? 'step' : undefined}
                className="flex items-center gap-3 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                    etape === s.n
                      ? 'bg-codialis-navy text-white'
                      : etape > s.n
                        ? 'gradient-mint text-white'
                        : 'bg-gray-100 text-codialis-gray'
                  }`}
                >
                  {etape > s.n ? <Check className="h-4 w-4" aria-hidden /> : s.n}
                </div>
                <span className={`text-sm ${etape === s.n ? 'font-bold text-codialis-navy' : 'text-codialis-gray'}`}>
                  {s.t}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {coursId && cours.isLoading && <Chargement libelle="Chargement du cours…" />}
      {coursId && cours.error && (
        <div className="space-y-3">
          <MessageErreur message={messageErreur(cours.error)} />
          <button
            type="button"
            onClick={() => setParametres({}, { replace: true })}
            className="text-sm font-semibold text-codialis-sky hover:underline"
          >
            Créer un nouveau cours
          </button>
        </div>
      )}

      {(!coursId || charge) && (
        <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
          <h2 className="font-display mb-4 text-lg font-bold text-codialis-navy">
            Étape {etape} : {ETAPES[etape - 1]!.t}
          </h2>

          {etape === 1 && (
            <form onSubmit={soumettre} className="space-y-4">
              {options.error && <MessageErreur message={messageErreur(options.error)} />}
              <fieldset disabled={Boolean(lectureSeule)} className="space-y-4 disabled:opacity-70">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cours-titre" className="mb-1 block text-xs font-semibold uppercase text-codialis-navy">
                      Titre du cours <MarqueObligatoire className="text-red-500" />
                    </label>
                    <input
                      id="cours-titre"
                      type="text"
                      required
                      maxLength={150}
                      value={saisie.titre}
                      onChange={(evenement) => setSaisie({ ...saisie, titre: evenement.target.value })}
                      className={CLASSE_CHAMP}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="cours-formation"
                      className="mb-1 block text-xs font-semibold uppercase text-codialis-navy"
                    >
                      Titre certifiant rattaché <MarqueObligatoire className="text-red-500" />
                    </label>
                    <select
                      id="cours-formation"
                      required
                      value={saisie.formationId}
                      // Le bloc suit le titre certifiant : l'API rend le cours transversal s'il change.
                      onChange={(evenement) => setSaisie({ ...saisie, formationId: evenement.target.value, blocId: '' })}
                      className={CLASSE_CHAMP}
                    >
                      {proposees.length === 0 && (
                        <option value="">{options.isLoading ? 'Chargement des formations…' : 'Aucune formation active'}</option>
                      )}
                      {proposees.map((formation) => (
                        <option key={formation.id} value={formation.id}>
                          {formation.titre}
                          {formation.rncp ? ` (RNCP ${formation.rncp})` : ''}
                          {'actif' in formation && !formation.actif ? ' · désactivée' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="cours-bloc" className="mb-1 block text-xs font-semibold uppercase text-codialis-navy">
                    Bloc du programme
                  </label>
                  <select
                    id="cours-bloc"
                    value={saisie.blocId}
                    onChange={(evenement) => setSaisie({ ...saisie, blocId: evenement.target.value })}
                    aria-describedby="cours-bloc-aide"
                    className={CLASSE_CHAMP}
                  >
                    <option value="">Plusieurs blocs (cours transversal)</option>
                    {blocs.map((bloc, rang) => (
                      <option key={bloc.id} value={bloc.id}>
                        Bloc {rang + 1} · {bloc.libelle}
                      </option>
                    ))}
                  </select>
                  <p id="cours-bloc-aide" className="mt-1 text-[11px] text-codialis-gray">
                    La progression des apprenants dans ce cours compte dans celle du bloc choisi.
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="cours-description"
                    className="mb-1 block text-xs font-semibold uppercase text-codialis-navy"
                  >
                    Description pédagogique
                  </label>
                  <textarea
                    id="cours-description"
                    rows={4}
                    maxLength={2000}
                    value={saisie.description}
                    onChange={(evenement) => setSaisie({ ...saisie, description: evenement.target.value })}
                    className={CLASSE_CHAMP}
                  />
                </div>
              </fieldset>
              {lectureSeule ? (
                <div className="flex flex-wrap justify-end gap-2 pt-4">
                  <button
                    type="button"
                    onClick={() => setEtape(2)}
                    className="min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep sm:px-6"
                  >
                    Étape suivante →
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap justify-end gap-2 pt-4">
                  <button
                    type="button"
                    onClick={() => void enregistrer(null)}
                    disabled={enregistrement.isPending}
                    className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-codialis-navy hover:bg-gray-50 disabled:opacity-60"
                  >
                    {charge?.statut === 'PUBLIE' ? 'Enregistrer' : 'Enregistrer le brouillon'}
                  </button>
                  <button
                    type="submit"
                    disabled={enregistrement.isPending}
                    className="min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60 sm:px-6"
                  >
                    Étape suivante →
                  </button>
                </div>
              )}
            </form>
          )}

          {etape === 2 && charge && (
            <div className="space-y-4">
              {lectureSeule ? (
                <ProgrammeEnLecture cours={charge} />
              ) : (
                <>
                  <p className="text-sm text-codialis-gray">
                    Structurez votre cours en modules et leçons. Saisissez la poignée pour les réorganiser, à la souris
                    ou au doigt ; une leçon peut passer d’un module à l’autre.
                  </p>
                  <ProgrammeCours cours={charge} />
                </>
              )}
            </div>
          )}

          {etape === 3 && charge && <ContenusCours cours={charge} />}
          {etape === 4 && charge && <EvaluationsCours cours={charge} />}

          {etape > 1 && (
            <div className="flex flex-wrap justify-between gap-2 pt-6">
              <button
                type="button"
                onClick={() => setEtape(etape - 1)}
                className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-codialis-navy hover:bg-gray-50"
              >
                ← Étape précédente
              </button>
              {etape < ETAPES.length && (
                <button
                  type="button"
                  onClick={() => setEtape(etape + 1)}
                  className="min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep sm:px-6"
                >
                  Étape suivante →
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
